import { useEffect, useState, useCallback } from "react";
import { Course } from "../../../../types/dataModel";
import { API, DIFFICULTY_LEVELS } from "../../../../common/Constants";
import useFetch from "../../../../hooks/useFetch";
import PreLoader from "../../../../core/common/preLoader";
import { getAPIHeaders, isNotEmpty } from "../../../../common/Utils";
import { Link } from "react-router-dom";
import CommonSelect from "../../../../core/common/commonSelect";
import CustomModal from "../../../../core/common/customModal";
import CommonSelectV2 from "../../../../core/common/commonSelectV2";

interface Props {
  onSuccess: () => void;
}

const AddCourse = ({ onSuccess }: Props) => {
  const [course, setCourse] = useState<Course>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");
  const [courseCategories, setCourseCategories] = useState<any[]>([]);
  const { isLoading, error, callFetch } = useFetch();
  const [showMsgBox, setShowMsgBox] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [learningHighlights, setLearningHighlights] = useState<string[]>([]);

  const fetchCourseCategories = useCallback(async () => {
    try {
      const response = await callFetch(API.SETTINGS_COURSE_CATEGORIES, 'GET', getAPIHeaders(), null, null);
      if (response) {
        setCourseCategories(response.map((ele:any)=>{
          return {
            value: ele.name,
            label: ele.name
          }
        }));
      } else {
        setCourseCategories([]);
      }
    } catch (err) {
      console.error("Failed to fetch course categories:", err);
    }
  }, []);

  useEffect(() => {
    fetchCourseCategories();
  }, []);

  useEffect(() => {
    if (error) {
      if (error.response?.data) {
        setErrorMessage(error.response?.data);
      } else {
        setErrorMessage("Something went wrong please try again later.");
      }
    }
  }, [error]);

  const addLearningHighlight = () => {
    const updatedHighlights = [...learningHighlights, ""];
    setLearningHighlights(updatedHighlights);
    setCourse({
      ...course,
      metadata: {
        ...course?.metadata,
        learning_highlights: updatedHighlights,
      },
    });
  };

  const removeLearningHighlight = (index: number) => {
    const updatedHighlights = learningHighlights.filter((_, i) => i !== index);
    setLearningHighlights(updatedHighlights);
    setCourse({
      ...course,
      metadata: {
        ...course?.metadata,
        learning_highlights: updatedHighlights,
      },
    });
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (!isNotEmpty(course?.title)) {
      setErrorMessage("Please provide title");
    } else if (!isNotEmpty(course?.description)) {
      setErrorMessage("Please provide description");
    } else if (!isNotEmpty(course?.code)) {
      setErrorMessage("Please provide code");
    } else if (!isNotEmpty(course?.type)) {
      setErrorMessage("Please select type");
    } else {
      setErrorMessage("");
      callFetch(API.COURSE, "POST", getAPIHeaders(), null, {
        ...course,
        metadata: {
          ...course?.metadata,
          learning_highlights: learningHighlights,
        },
      }).then(()=>{
        reset();
        setShowMsgBox(true);
        onSuccess();
      }).catch((err) => {});;
    }
  };

  const reset = () => {
    setCourse({
      title: "",
      description: "",
      code: "",
      type: "",
      isCommercial: false,
      metadata: {
        thumbnail_url: "",
        difficulty_level: "",
        learning_highlights: []
      }
    });
    setErrorMessage("");
    setShowMsgBox(false);
    setShowPreviewModal(false);
    setLearningHighlights([]);
  };

  const renderBasicInfoForm = () => {
    return (
      <div
        className="tab-pane fade show active" 
        id="basic-info"
        role="tabpanel"
        aria-labelledby="info-tab"
        tabIndex={0}
      >
        <div className="modal-body pb-0">
          <div className="row">
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">
                  Title <span className="text-danger"> *</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={course?.title}
                  onChange={(e) => {
                    setCourse({
                      ...course,
                      title: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Description <span className="text-danger"> *</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={course?.description}
                  onChange={(e) => {
                    setCourse({
                      ...course,
                      description: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">
                  Code <span className="text-danger"> *</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={course?.code}
                  onChange={(e) => {
                    setCourse({
                      ...course,
                      code: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">
                Category <span className="text-danger"> *</span>
                </label>
                <CommonSelect
                  className="select"
                  options={courseCategories}
                  onChange={(value) => {
                    setCourse({
                      ...course,
                      type: value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="col-md-12">
              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isCommercial"
                    checked={course?.isCommercial || false}
                    onChange={(e) => {
                      setCourse({
                        ...course,
                        isCommercial: e.target.checked,
                      });
                    }}
                  />
                  <label className="form-check-label" htmlFor="isCommercial">
                    Commercial Course
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer d-flex justify-content-between align-items-center">
          <div style={{ maxWidth: "60%" }}>
            {errorMessage ? (
              <>
                <i className="ti ti-alert-triangle text-danger" />
                <span className="text-danger"> {errorMessage} </span>
              </>
            ) : (
              <></>
            )}
          </div>
          <div>
            <button
              type="button"
              className="btn btn-light me-2"
              data-bs-dismiss="modal"
              onClick={reset}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              Add Course
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderMetadataForm = () => {
    return (
      <div
        className="tab-pane fade"
        id="metadata-info"
        role="tabpanel"
        aria-labelledby="metadata-tab"
        tabIndex={0}
      >
        <div className="modal-body pb-0">
          <div className="row">
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Thumbnail URL</label>
                <div className="input-group">
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://example.com/thumbnail.jpg"
                    value={course?.metadata?.thumbnail_url || ""}
                    onChange={(e) => {
                      setCourse({
                        ...course,
                        metadata: {
                          ...course?.metadata,
                          thumbnail_url: e.target.value,
                        },
                      });
                    }}
                  />
                  {course?.metadata?.thumbnail_url && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPreviewModal(true)}
                      title="Preview Thumbnail"
                      style={{ zIndex: 0 }}
                    >
                      <i className="ti ti-eye" />
                    </button>
                  )}
                  </div>
                  <div className="mt-2 bg-light-500 rounded p-2">
                     <small className="text-info">Note: Thumbnail should be in 16:9 ratio.</small>
                  </div>
                </div>
            </div>
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Difficulty Level</label>
                <CommonSelectV2
                  options={DIFFICULTY_LEVELS}
                  defaultValue={DIFFICULTY_LEVELS[0]}
                  value={course?.metadata?.difficulty_level ? { value: course.metadata.difficulty_level, label: course.metadata.difficulty_level } : null}
                  onChange={(value) => {
                    setCourse({
                      ...course,
                      metadata: {
                        ...course?.metadata,
                        difficulty_level: value,
                      },
                    });
                  }}
                  placeHolder="Select Difficulty Level"
                />
              </div>
            </div>
            <div className="col-md-12">
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0">Learning Highlights</label>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={addLearningHighlight}
                  >
                    <i className="ti ti-plus" />
                  </button>
                </div>
                {learningHighlights.map((highlight, index) => (
                  <div key={index} className="input-group mb-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter a learning highlight"
                      value={highlight}
                      onChange={(e) => {
                        const updatedHighlights = [...learningHighlights];
                        updatedHighlights[index] = e.target.value;
                        setLearningHighlights(updatedHighlights);
                        setCourse({
                          ...course,
                          metadata: {
                            ...course?.metadata,
                            learning_highlights: updatedHighlights,
                          },
                        });
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => removeLearningHighlight(index)}
                    >
                      <i className="ti ti-x" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <div style={{ maxWidth: "50%" }}>
            {errorMessage ? (
              <>
                <i className="ti ti-alert-triangle text-danger" />
                <span className="text-danger"> {errorMessage} </span>
              </>
            ) : (
              <></>
            )}
          </div>
          <div>
            <button
              type="button"
              className="btn btn-light me-2"
              data-bs-dismiss="modal"
              onClick={reset}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
            >
              Add Course
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <div className="modal-content">
        <div className="modal-header">
          <h4 className="modal-title">Add Course</h4>
          <button
            type="button"
            className="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={reset}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <form>
          <div className="contact-grids-tab">
            <ul className="nav nav-underline" id="myTab" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link active"
                  id="info-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#basic-info"
                  type="button"
                  role="tab"
                  aria-selected="true"
                  onClick={() => setErrorMessage("")}
                >
                  Basic Information
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link"
                  id="metadata-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#metadata-info"
                  type="button"
                  role="tab"
                  aria-selected="false"
                  onClick={() => setErrorMessage("")}
                >
                  Metadata
                </button>
              </li>
            </ul>
          </div>
          <div className="tab-content" id="myTabContent">
            {renderBasicInfoForm()}
            {renderMetadataForm()}
          </div>
        </form>
      </div>
    );
  };

  const renderMsgBox = () => {
    return (
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body text-center">
            <span className="avatar avatar-xl bg-transparent-success text-success mb-3">
              <i className="ti ti-thumb-up fs-36" />
            </span>
            <h4 className="mb-1">Add Course Successfull</h4>
            <p className="mb-3">Course is added successfully.</p>
            <div className="d-flex justify-content-center">
              <Link
                to="#"
                data-bs-dismiss="modal"
                className="btn btn-success"
                onClick={() => {
                  setTimeout(() => {
                    setShowMsgBox(false);
                  }, 1000);
                }}
              >
                Close
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPreviewModal = () => {
    return (
      <CustomModal
        show={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Thumbnail Preview"
        size="lg"
        bodyContent={
          <div className="text-center">
            <img
              src={course?.metadata?.thumbnail_url}
              alt="Thumbnail Preview"
              className="img-fluid rounded"
              style={{ maxHeight: "400px", maxWidth: "100%" }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                const errorDiv = document.createElement("div");
                errorDiv.className = "alert alert-warning";
                errorDiv.textContent = "Failed to load image. Please check the URL.";
                target.parentNode?.appendChild(errorDiv);
              }}
            />
            <div className="mt-3">
              <small className="text-muted">
                URL: {course?.metadata?.thumbnail_url}
              </small>
            </div>
          </div>
        }
        footerContent={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowPreviewModal(false)}
          >
            Close
          </button>
        }
      />
    );
  };

  return (
    <>
      <div
        className="modal fade"
        id="add_course"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <PreLoader showLoader={isLoading} />
          {showMsgBox ? renderMsgBox() : renderForm()}
        </div>
      </div>
      {renderPreviewModal()}
    </>
  );
};

export default AddCourse;
